import React, { ReactNode, useMemo } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';

type WithButtons = {
  buttons: ReactNode;
};
type WithDefaultButton = {
  buttonText: ReactNode;
  buttonProps?: ButtonProps;
  variant?: ButtonProps['variant'];
};

type ButtonsConfig = WithButtons | WithDefaultButton;

interface BaseModalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  text: ReactNode;
}

export type ModalDialogProps = BaseModalDialogProps & ButtonsConfig;

function withButtons(props: ModalDialogProps): props is BaseModalDialogProps & WithButtons {
  return !!(props as WithButtons).buttons;
}

export const ModalDialog = (props: ModalDialogProps) => {
  const { title, text, isOpen, onClose } = props;

  const actions = useMemo(() => {
    if (withButtons(props)) {
      return props.buttons;
    }
    return (
      <Button variant={props.variant} onClick={props.onClose} {...props.buttonProps}>
        {props.buttonText}
      </Button>
    );
  }, [props]);

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle id="alert-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h5" color="textPrimary">
              {title}
            </Typography>
          </Box>
          <Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText variant="bodyM">{text}</DialogContentText>
      </DialogContent>
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  );
};
